import { useMemo } from 'react';
import type { ComponentWithValues } from 'common/stack/ComponentWithValuesDocuments';

interface CreateStackComponentSortFunctionParams {
  components: ComponentWithValues[];
}

export const createStackComponentSortFunction =
  ({ components }: CreateStackComponentSortFunctionParams) =>
  <Component extends { stackComponentID: number | null }>(a: Component, b: Component) => {
    let ai = components.findIndex((component) => component.stackComponentID === a.stackComponentID);
    if (ai < 0) ai = Number.MAX_VALUE;
    let bi = components.findIndex((component) => component.stackComponentID === b.stackComponentID);
    if (bi < 0) bi = Number.MAX_VALUE;
    return ai - bi;
  };

interface CreateStackComponentFilterFunctionParams {
  components: ComponentWithValues[];
  filterByType: 'component' | 'addon';
}

export const createStackComponentFilterFunction =
  ({ components, filterByType }: CreateStackComponentFilterFunctionParams) =>
  <Component extends { stackComponentID: number | null }>({ stackComponentID }: Component) => {
    const component = components.find((component) => component.stackComponentID === stackComponentID);
    if (!component) return false;
    return component.config?.type === filterByType;
  };

export const useSortedItems = <Item extends { stackComponentID: number | null }>({
  items = [],
  components,
}: {
  items?: Item[];
  components: ComponentWithValues[];
}) =>
  useMemo<(Item & { component?: ComponentWithValues })[]>(
    () =>
      items
        .filter(Boolean)
        .map((item) => ({ ...item, component: components.find((component) => component.stackComponentID === item.stackComponentID) }))
        .toSorted(createStackComponentSortFunction({ components })),
    [items, components]
  );
